import { useEffect, useId, useState } from 'react';
import { taxonomyRepository, type TaxonomyLabels } from '../../data/repositories/taxonomyRepository';
import type { ExerciseRecord } from '../../data/types';
import styles from './Exercises.module.css';

/**
 * The "Targets" and "Instructions" sections — extracted out of
 * `ExerciseDetailScreen` (plan-edit info panel fix, 2026-08-26) so the plan
 * editor's exercise info panel can show the exact same content without a
 * second implementation, the same reasoning `ExerciseDemonstration` already
 * follows for the picture/video half of "exercise information."
 *
 * Self-contained (fetches its own taxonomy labels) so a caller only ever
 * needs `<ExerciseInfoDetails exercise={exercise} />` — no extra wiring.
 *
 * `useId()` rather than the literal `id="detail-targets"` this had inline in
 * `ExerciseDetailScreen`: that screen only ever renders one exercise at a
 * time, but a plan can have several rows with their info panels open at
 * once, and duplicate ids on one page break `aria-labelledby`.
 */
export function ExerciseInfoDetails({ exercise }: { exercise: ExerciseRecord }): React.JSX.Element {
  const [labels, setLabels] = useState<TaxonomyLabels | null>(null);
  const targetsHeadingId = useId();
  const instructionsHeadingId = useId();

  useEffect(() => {
    let cancelled = false;
    void taxonomyRepository.labels().then((loaded) => {
      if (!cancelled) setLabels(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const label = (kind: 'bodyPart' | 'muscle' | 'equipment', slug: string): string =>
    labels === null ? slug : labels[kind](slug);

  const chips = (kind: 'bodyPart' | 'muscle' | 'equipment', slugs: string[]): React.JSX.Element => (
    <span className={styles.itemMeta}>
      {slugs.map((slug) => (
        <span key={slug} className={styles.chip}>
          {label(kind, slug)}
        </span>
      ))}
    </span>
  );

  return (
    <>
      <section className={styles.section} aria-labelledby={targetsHeadingId}>
        <h2 id={targetsHeadingId} className={styles.sectionTitle}>
          Targets
        </h2>
        <dl className={styles.definitions}>
          <div className={styles.detailRow}>
            <dt>Body part</dt>
            <dd>{chips('bodyPart', exercise.bodyParts)}</dd>
          </div>
          <div className={styles.detailRow}>
            <dt>Target muscles</dt>
            <dd>{chips('muscle', exercise.targetMuscles)}</dd>
          </div>
          {exercise.secondaryMuscles.length > 0 && (
            <div className={styles.detailRow}>
              <dt>Also works</dt>
              <dd>{chips('muscle', exercise.secondaryMuscles)}</dd>
            </div>
          )}
          <div className={styles.detailRow}>
            <dt>Equipment</dt>
            <dd>{chips('equipment', exercise.equipment)}</dd>
          </div>
          <div className={styles.detailRow}>
            <dt>Type</dt>
            <dd>
              <span className={styles.itemMeta}>
                <span className={styles.chip}>
                  {exercise.modality} · {exercise.loadType.replace(/_/g, ' ')}
                </span>
              </span>
            </dd>
          </div>
        </dl>
      </section>

      {exercise.instructions.length > 0 && (
        <section className={styles.section} aria-labelledby={instructionsHeadingId}>
          <h2 id={instructionsHeadingId} className={styles.sectionTitle}>
            Instructions
          </h2>
          <ol className={styles.instructions}>
            {exercise.instructions.map((step, index) => (
              <li key={index} className={styles.instructionStep}>
                {step}
              </li>
            ))}
          </ol>
        </section>
      )}
    </>
  );
}
